// GA4's limits on what an event may be called and carry, applied before
// anything is sent rather than silently dropped by the tag: names in
// snake_case (letters, digits, underscores, starting with a letter), an
// event or parameter name 40 characters at most, a parameter value 100, 25
// parameters per event. What does not fit is cut or left out, never thrown.
import type { AnalyticsValue } from '@chess/api-analytics';

export const MAX_NAME = 40;
export const MAX_VALUE = 100;
export const MAX_PARAMS = 25;

/** A name GA4 accepts, or undefined when nothing is left of it. */
export function sanitizeName(raw: string): string | undefined {
  const name = raw
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+/g, '_')
    .replace(/_+$/, '')
    .slice(0, MAX_NAME)
    .replace(/_+$/, '');
  return name || undefined;
}

/** A value within the limit: a string cut to 100 characters, a number or boolean as it is; an empty string is nothing. */
export function sanitizeValue(value: AnalyticsValue): AnalyticsValue | undefined {
  if (typeof value !== 'string') return Number.isFinite(value) || typeof value === 'boolean' ? value : undefined;
  const s = value.replace(/\s+/g, ' ').trim().slice(0, MAX_VALUE).trim();
  return s || undefined;
}

export interface SanitizedEvent {
  name: string;
  params: Record<string, AnalyticsValue>;
}

/** The event as GA4 may receive it, or undefined when its name is unusable. Parameters past the first 25 that fit are left out. */
export function sanitizeEvent(event: string, params: Record<string, AnalyticsValue | undefined> = {}): SanitizedEvent | undefined {
  const name = sanitizeName(event);
  if (!name) return undefined;
  const clean: Record<string, AnalyticsValue> = {};
  let count = 0;
  for (const [key, raw] of Object.entries(params)) {
    if (count >= MAX_PARAMS) break;
    if (raw === undefined) continue;
    const k = sanitizeName(key);
    const v = sanitizeValue(raw);
    if (!k || v === undefined || k in clean) continue;
    clean[k] = v;
    count++;
  }
  return { name, params: clean };
}
